import type { OnlineModel } from '../../../generated/api/onlineModel';
import type { OnlineModelHeader } from '../../../generated/api/onlineModelHeader';

type OnlineModelsByCode = Record<string, OnlineModel>;
type OnlineModelHeadersByCode = Record<string, OnlineModelHeader>;

export interface OnlineModelsState {
  onlineModelsByCode?: OnlineModelsByCode;
  onlineModelHeadersByCode?: OnlineModelHeadersByCode;
  onlineModelsByVoucher?: Record<string, OnlineModelsByCode>;
}
export enum OnlineModelQueryActionType {
  GET_PRIVATE_ONLINE_MODELS_SUCCESS,
  GET_PRIVATE_ONLINE_MODEL_HEADERS_SUCCESS,
  GET_PUBLIC_ONLINE_MODELS_SUCCESS,
}
export interface OnlineModelQueryAction {
  type: OnlineModelQueryActionType;
  models?: OnlineModel[];
  headers?: OnlineModelHeader[];
  onlineModelCodes?: string[];
  voucherCode?: string;
}

/**
 * TODO BULK-API: Remove when private/bulk API returns correct campaign data.
 * Merges campaign data from existing model into incoming model when incoming has empty campaigns.
 */
const preserveCampaignsIfMissing = (incoming: OnlineModel, existing: OnlineModel | undefined): OnlineModel => {
  if (
    !existing?.campaigns ||
    existing.campaigns.length === 0 ||
    (incoming.campaigns && incoming.campaigns.length > 0)
  ) {
    return incoming;
  }

  return {
    ...incoming,
    campaigns: existing.campaigns,
    offers: incoming.offers?.map(offer => {
      const existingOffer = existing.offers?.find(o => o.offerCode === offer.offerCode);
      if (!existingOffer) {
        return offer;
      }
      return {
        ...offer,
        commercialProducts: offer.commercialProducts?.map((cp, i) => {
          const existingCp = existingOffer.commercialProducts?.[i];
          if (cp.campaignAssociations && cp.campaignAssociations.length > 0) {
            return cp;
          }
          if (existingCp?.campaignAssociations && existingCp.campaignAssociations.length > 0) {
            return { ...cp, campaignAssociations: existingCp.campaignAssociations };
          }
          return cp;
        }),
      };
    }),
  };
};

/**
 * Merges incoming models into state, applying campaign preservation and Liikkuva WiFi filtering.
 */
const mergeModels = (incoming: OnlineModel[], existing: OnlineModelsByCode): OnlineModelsByCode => {
  return incoming.reduce<Record<string, OnlineModel>>(
    (acc, obj) => {
      acc[obj.onlineModelCode] = preserveCampaignsIfMissing(obj, acc[obj.onlineModelCode]);
      return acc;
    },
    { ...existing }
  );
};

/**
 * Merges incoming headers into state.
 */
const mergeHeaders = (incoming: OnlineModelHeader[], existing: OnlineModelHeadersByCode): OnlineModelHeadersByCode => {
  return incoming.reduce<Record<string, OnlineModelHeader>>(
    (acc, obj) => {
      acc[obj.onlineModelCode] = obj;
      return acc;
    },
    { ...existing }
  );
};

const updateReducerByVoucher = (
  state: OnlineModelsState,
  action: OnlineModelQueryAction,
  voucherCode: string
): OnlineModelsState => {
  switch (action.type) {
    case OnlineModelQueryActionType.GET_PRIVATE_ONLINE_MODELS_SUCCESS: {
      const existing = state.onlineModelsByVoucher?.[voucherCode] ?? { ...(state.onlineModelsByCode ?? {}) };
      return {
        ...state,
        onlineModelsByVoucher: {
          ...(state.onlineModelsByVoucher ?? {}),
          [voucherCode]: mergeModels(action.models ?? [], existing),
        },
      };
    }
    default:
      return { ...state };
  }
};

export const onlineModelReducer = (state: OnlineModelsState, action: OnlineModelQueryAction): OnlineModelsState => {
  const voucherCode = action.voucherCode;
  if (voucherCode) {
    return updateReducerByVoucher(state, action, voucherCode);
  }

  switch (action.type) {
    case OnlineModelQueryActionType.GET_PRIVATE_ONLINE_MODELS_SUCCESS:
    case OnlineModelQueryActionType.GET_PUBLIC_ONLINE_MODELS_SUCCESS:
      return {
        ...state,
        onlineModelsByCode: mergeModels(action.models ?? [], state?.onlineModelsByCode ?? {}),
      };
    case OnlineModelQueryActionType.GET_PRIVATE_ONLINE_MODEL_HEADERS_SUCCESS:
      return {
        ...state,
        onlineModelHeadersByCode: mergeHeaders(action.headers ?? [], state?.onlineModelHeadersByCode ?? {}),
      };
    default:
      return { ...state };
  }
};
